import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Edit, Trash2, X, Share2, Save } from "lucide-react";
import CustomDatePicker from "./CustomDatePicker";
import { useModal } from "../contexts/ModalContext";
import {
    getLogIcon,
    getTypeColor,
    getSentimentIcon as getSentimentIconUtil,
} from "../views/counseling/counselingUiUtils";

export default function VisitLogModal({
    log,
    onClose,
    onUpdate,
    onDelete,
    userProfile,
    theme
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const { openConfirm, openAlert } = useModal();

    useEffect(() => {
        if (log) {
            setEditForm({ ...log });
            setIsEditing(false);
        }
    }, [log]);

    if (!log) return null;

    // Permissions logic
    const isTeacher = userProfile?.memberRole === 'teacher';
    const currentUserId = userProfile?.uid;
    const canEdit = !isTeacher || log.createdBy === currentUserId;

    const colors = getTypeColor(isEditing ? editForm.type : log.type);
    const iconSrc = getLogIcon(isEditing ? editForm.type : log.type);

    const handleSave = () => {
        if (!editForm.content.trim()) return openAlert("내용을 입력해주세요.", "알림");
        if (onUpdate) onUpdate(editForm);
        setIsEditing(false);
        openAlert("수정되었습니다.", "알림");
    };

    const handleShare = () => {
        const text = `[심방 일지]\n날짜: ${log.date}\n학생: ${log.studentName}\n유형: ${log.type}\n내용:\n${log.content}`;
        navigator.clipboard.writeText(text).then(() => openAlert("일지 내용이 복사되었습니다!", "알림"));
    };

    const handleDelete = () => {
        openConfirm("기록 삭제", "정말 이 기록을 삭제하시겠습니까?", () => {
            if (onDelete) onDelete(log.id);
            onClose();
        });
    };

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[10000] p-4 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
            <div
                className={`bg-white p-0 w-full max-w-sm shadow-2xl relative overflow-hidden border-4 ${colors.border} rounded-[2.5rem] animate-fade-in-up`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header Section */}
                <div className={`bg-gradient-to-br from-white to-${colors.bg.replace("bg-", "")} p-6 pb-8 relative`}>
                    <button onClick={onClose} className="absolute top-5 right-5 text-gray-400 hover:text-gray-600 bg-white/50 hover:bg-white p-2 rounded-full transition backdrop-blur-md z-50">
                        <X size={20} />
                    </button>
                    
                    <div className="flex flex-col items-center relative z-10">
                        <div className={`w-20 h-20 rounded-[1.5rem] bg-white shadow-[0_8px_20px_rgba(0,0,0,0.08)] flex items-center justify-center mb-4 ${colors.text} border-4 border-white`}>
                            <img src={iconSrc} alt={log.type} className="w-10 h-10 object-contain drop-shadow-sm" />
                        </div>

                        {isEditing ? (
                            <div className="flex flex-col items-center gap-2 w-full">
                                <div className="flex gap-1 justify-center w-full overflow-x-auto pb-1 no-scrollbar">
                                    {["면담", "전화", "문자", "기도", "교제"].map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => setEditForm({ ...editForm, type: t })}
                                            className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-all ${editForm.type === t ? "bg-gray-800 text-white" : "bg-white/50 text-gray-500 hover:bg-white"}`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                                <div className="w-[140px] z-50 relative">
                                    <CustomDatePicker
                                        theme={theme}
                                        value={editForm.date}
                                        onChange={(d) => setEditForm({ ...editForm, date: d })}
                                        className="w-full shadow-sm bg-white/60 text-xs font-bold border border-white/60 rounded-2xl text-center py-2 flex justify-center items-center"
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-2xl font-black text-gray-800 font-do-hyeon drop-shadow-sm">{log.studentName}</h3>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs font-bold text-gray-400 bg-white/60 px-3 py-1 rounded-full shadow-sm uppercase tracking-wider">{log.date}</span>
                                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border border-current opacity-80 ${colors.text} ${colors.bg}`}>
                                        {log.type}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Decorative Blobs */}
                    <div className="absolute top-0 left-0 w-full h-full opacity-10 overflow-hidden pointer-events-none">
                        <div className={`absolute -top-10 -left-10 w-40 h-40 rounded-full ${colors.bg} blur-3xl`}></div>
                        <div className={`absolute top-10 right-0 w-32 h-32 rounded-full ${colors.bg} blur-2xl`}></div>
                    </div>
                </div>

                {/* Body Section */}
                <div className="p-8 pt-6 relative -mt-4 bg-white rounded-t-[2rem]">
                    <div className="flex justify-center -mt-10 mb-6 relative z-10">
                        {isEditing ? (
                            <div className="flex flex-col items-center gap-1">
                                <div className="bg-white p-1.5 rounded-2xl shadow-sm ring-1 ring-gray-100 flex gap-1">
                                    {["happy", "neutral", "sad"].map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setEditForm({ ...editForm, sentiment: s })}
                                            className={`p-2 rounded-xl transition-all duration-300 ${editForm.sentiment === s ? "bg-gray-100 scale-110 shadow-inner" : "opacity-40 hover:opacity-100"}`}
                                        >
                                            {getSentimentIconUtil(s)}
                                        </button>
                                    ))}
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2.5 py-1 rounded-lg">
                                    {editForm.sentiment === "happy" ? "기쁨 😊" : editForm.sentiment === "sad" ? "슬픔 😢" : "평온 😐"}
                                </span>
                            </div>
                        ) : (
                            <div className="bg-white p-2 rounded-2xl shadow-sm ring-1 ring-gray-100">{getSentimentIconUtil(log.sentiment)}</div>
                        )}
                    </div>

                    <div className={`relative bg-gray-50/50 rounded-2xl p-6 min-h-[140px] max-h-[300px] overflow-hidden mb-6 border ${colors.border} shadow-inner group`}>
                        <div className="absolute -bottom-2 -right-2 opacity-[0.1] transform rotate-[-15deg] group-hover:scale-110 group-hover:opacity-20 transition-all duration-500">
                            <img src={iconSrc} alt="" className="w-32 h-32 object-contain grayscale" />
                        </div>

                        <div className="relative z-10 overflow-y-auto custom-scrollbar max-h-[260px] pr-2">
                            {isEditing ? (
                                <textarea
                                    value={editForm.content}
                                    onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                                    className="w-full h-[140px] bg-transparent border-none outline-none resize-none text-gray-700 font-medium leading-relaxed text-[15px]"
                                    placeholder="어떤 대화를 나누셨나요?"
                                />
                            ) : (
                                <p className="text-gray-700 font-medium leading-relaxed whitespace-pre-wrap text-[15px]">{log.content}</p>
                            )}
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex flex-col gap-3">
                        {isEditing ? (
                            <div className="flex gap-3">
                                <button onClick={handleSave} className={`flex-1 py-3.5 rounded-2xl font-bold transition flex items-center justify-center gap-2 border shadow-sm ${colors.bg} ${colors.text} ${colors.border} hover:brightness-95`}>
                                    <CheckCircle2 size={18} /> 저장
                                </button>
                                <button onClick={() => setIsEditing(false)} className="flex-1 py-3.5 rounded-2xl bg-white border border-gray-200 text-gray-400 font-bold hover:bg-gray-50 transition flex items-center justify-center gap-2 shadow-sm">
                                    <X size={18} /> 취소
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex gap-3">
                                    {canEdit ? (
                                        <>
                                            <button onClick={() => setIsEditing(true)} className={`flex-1 py-3.5 rounded-2xl font-bold transition flex items-center justify-center gap-2 border shadow-sm ${colors.bg} ${colors.text} ${colors.border} hover:brightness-95`}>
                                                <Edit size={18} /> 수정
                                            </button>
                                            <button
                                                onClick={handleDelete}
                                                className="flex-1 py-3.5 rounded-2xl bg-white border border-gray-200 text-gray-400 font-bold hover:bg-rose-50 hover:text-rose-500 hover:border-rose-200 transition flex items-center justify-center gap-2 shadow-sm"
                                            >
                                                <Trash2 size={18} /> 삭제
                                            </button>
                                        </>
                                    ) : (
                                        <button onClick={handleShare} className="flex-1 py-3.5 rounded-2xl bg-[#FFECA1] text-[#4A3B10] font-bold text-sm shadow-sm hover:bg-[#FFD54F] transition flex items-center justify-center gap-2">
                                            <Share2 size={18} /> 내용 공유하기
                                        </button>
                                    )}
                                </div>
                                <button onClick={onClose} className="w-full py-4 bg-gray-50 text-gray-400 border border-gray-100 rounded-2xl font-bold hover:bg-gray-100 transition">닫기</button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
